import React, { useState, useEffect } from 'react';
import { Form, Button, Alert, Spinner } from 'react-bootstrap';
import { submitQuestion } from '../services/questionService';
import { useTracksForAsk } from '../hooks/useTracks';
import './AskQuestion.scss';

const AskQuestion = () => {
  const [formData, setFormData] = useState({
    title: '',
    question: '',
    track: 'general'
  });
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  
  // Get dynamic tracks including general option
  const { options: trackOptions, loading: tracksLoading } = useTracksForAsk();
  
  // Update default track when options load
  useEffect(() => {
    if (trackOptions.length > 0 && !formData.track) {
      setFormData(prev => ({
        ...prev,
        track: 'general' // Default to general for ask page
      }));
    }
  }, [trackOptions, formData.track]);

  const handleChange = (e) => {
    setFormData({ ...formData, [e.target.name]: e.target.value });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setSuccess('');
    setLoading(true);

    try {
      // Validate form data
      if (!formData.title.trim()) {
        throw new Error('Title is required');
      }

      if (!formData.question.trim()) {
        throw new Error('Question is required');
      }

      // Submit question
      await submitQuestion(
        formData.title,
        formData.question,
        formData.track
      );

      // Show success message
      setSuccess('Your question has been submitted successfully! We will respond as soon as possible.');
      
      // Reset form
      setFormData({
        title: '',
        question: '',
        track: 'general'
      });
    } catch (err) {
      setError(err.message || 'An error occurred while submitting your question');
    } finally {
      setLoading(false);
    }
  };

  return (
    <>
      {error && <Alert variant="danger">{error}</Alert>}
      {success && <Alert variant="success">{success}</Alert>}
      
      <Form onSubmit={handleSubmit}>
        <Form.Group className="mb-3">
          <Form.Label>Title *</Form.Label>
          <Form.Control
            type="text"
            name="title"
            value={formData.title}
            onChange={handleChange}
            placeholder="Enter a brief title for your question"
            required
          />
        </Form.Group>

        <Form.Group className="mb-3">
          <Form.Label>Question *</Form.Label>
          <Form.Control
            as="textarea"
            rows={6}
            name="question"
            value={formData.question}
            onChange={handleChange}
            placeholder="Describe your question in detail"
            required
          />
        </Form.Group>

        <Form.Group className="mb-3">
          <Form.Label>Track</Form.Label>
          <Form.Select
            name="track"
            value={formData.track}
            onChange={handleChange}
            disabled={tracksLoading}
          >
            {tracksLoading ? (
              <option>Loading tracks...</option>
            ) : (
              trackOptions.map(option => (
                <option key={option.value} value={option.value}>
                  {option.label}
                </option>
              ))
            )}
          </Form.Select>
          <Form.Text className="text-muted">
            {tracksLoading ? (
              <>
                <Spinner size="sm" className="me-1" />
                Loading track configuration...
              </>
            ) : (
              'Select the track related to your question'
            )}
          </Form.Text>
        </Form.Group>

        <Button 
          variant="primary" 
          type="submit" 
          disabled={loading || tracksLoading}
          className="submit-button"
        >
          {loading ? (
            <>
              <Spinner
                as="span"
                animation="border"
                size="sm"
                role="status"
                aria-hidden="true"
                className="me-2"
              />
              Sending...
            </>
          ) : (
            'Send the Question'
          )}
        </Button>
      </Form>
    </>
  );
};

export default AskQuestion;
